/**
 * Single source of truth for Country / State / City dropdowns.
 *
 * Backed by the `country-state-city` dataset (250 countries, ~5000 states,
 * ~150k cities). Country + state data is small and imported eagerly; the city
 * dataset is 8 MB of JSON so it is code-split and fetched on demand the first
 * time a city dropdown actually needs it (see `ensureCitiesLoaded`).
 *
 * Values stored in the database are plain NAMES (not ISO codes) so existing
 * lead / project rows keep working. Legacy short names ("UAE", "USA", "UK")
 * still resolve to the right country via ALIASES.
 */

import CountryLib from 'country-state-city/lib/cjs/country';
import StateLib from 'country-state-city/lib/cjs/state';

// ---------------------------------------------------------------- countries

const ALL_COUNTRIES = CountryLib.getAllCountries();

export const COUNTRY_OPTIONS = ALL_COUNTRIES
  .map((c) => ({ value: c.name, label: c.name, iso: c.isoCode }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Short forms that older records were saved with.
const ALIASES = {
  uae: 'AE',
  'u.a.e': 'AE',
  'u.a.e.': 'AE',
  usa: 'US',
  'u.s.a': 'US',
  'u.s.a.': 'US',
  us: 'US',
  america: 'US',
  uk: 'GB',
  'u.k': 'GB',
  'u.k.': 'GB',
  england: 'GB',
  britain: 'GB',
  'great britain': 'GB',
  russia: 'RU',
  'south korea': 'KR',
  'north korea': 'KP',
  vietnam: 'VN',
  syria: 'SY',
  laos: 'LA',
  moldova: 'MD',
  tanzania: 'TZ',
  bolivia: 'BO',
  venezuela: 'VE',
  iran: 'IR',
  taiwan: 'TW',
  macau: 'MO',
  'hong kong': 'HK',
  'ivory coast': 'CI',
  'cape verde': 'CV',
  'czech republic': 'CZ',
  swaziland: 'SZ',
  turkey: 'TR',
  brunei: 'BN',
};

const COUNTRY_NAME_TO_ISO = (() => {
  const map = { ...ALIASES };
  ALL_COUNTRIES.forEach((c) => {
    map[c.name.toLowerCase()] = c.isoCode;
    map[c.isoCode.toLowerCase()] = c.isoCode;
  });
  return map;
})();

export const isoForCountry = (countryName) =>
  countryName ? COUNTRY_NAME_TO_ISO[String(countryName).trim().toLowerCase()] || '' : '';

// ------------------------------------------------------------------- states

// "Abu Dhabi Emirate" -> "Abu Dhabi", "Bangkok Province" -> "Bangkok".
// Only applied when the shortened name stays unique inside that country.
const REGION_SUFFIX = /\s+(Emirate|Province|Governorate|Prefecture|Municipality|Region|District|Oblast|Voivodeship|County)$/i;

const stateCache = new Map(); // iso -> { options, byName }

const buildStates = (iso) => {
  const raw = StateLib.getStatesOfCountry(iso) || [];
  const shortCount = new Map();
  raw.forEach((s) => {
    const short = s.name.replace(REGION_SUFFIX, '').trim();
    shortCount.set(short, (shortCount.get(short) || 0) + 1);
  });

  const byName = {};
  const options = raw
    .map((s) => {
      const short = s.name.replace(REGION_SUFFIX, '').trim();
      const label = short && shortCount.get(short) === 1 ? short : s.name;
      byName[label.toLowerCase()] = s.isoCode;
      byName[s.name.toLowerCase()] = s.isoCode;
      byName[s.isoCode.toLowerCase()] = s.isoCode;
      return { value: label, label, iso: s.isoCode };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return { options, byName };
};

const statesFor = (iso) => {
  if (!iso) return { options: [], byName: {} };
  if (!stateCache.has(iso)) stateCache.set(iso, buildStates(iso));
  return stateCache.get(iso);
};

export const getStateOptions = (countryName) => statesFor(isoForCountry(countryName)).options;

export const stateCodeFor = (countryName, stateName) => {
  if (!stateName) return '';
  const { byName } = statesFor(isoForCountry(countryName));
  return byName[String(stateName).trim().toLowerCase()] || '';
};

// ------------------------------------------------------------------- cities

let CityLib = null;
let cityLoadPromise = null;

export const citiesReady = () => CityLib !== null;

/** Lazily pulls in the 8 MB city dataset as its own webpack chunk. */
export const ensureCitiesLoaded = () => {
  if (CityLib) return Promise.resolve(CityLib);
  if (!cityLoadPromise) {
    cityLoadPromise = import(
      /* webpackChunkName: "city-data" */ 'country-state-city/lib/cjs/city'
    )
      .then((mod) => {
        CityLib = mod.default || mod;
        return CityLib;
      })
      .catch((err) => {
        cityLoadPromise = null;
        throw err;
      });
  }
  return cityLoadPromise;
};

const cityCache = new Map(); // `${iso}:${stateCode}` -> options

const toOptions = (cities) =>
  cities
    .map((c) => c.name)
    .filter((name, i, arr) => arr.indexOf(name) === i)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));

/**
 * Cities of a state. Returns [] until `ensureCitiesLoaded()` has resolved.
 * When no state is selected, falls back to every city in the country.
 */
export const getCityOptions = (countryName, stateName) => {
  if (!CityLib) return [];
  const iso = isoForCountry(countryName);
  if (!iso) return [];
  const stateCode = stateCodeFor(countryName, stateName);
  const key = `${iso}:${stateCode}`;
  if (cityCache.has(key)) return cityCache.get(key);

  const raw = stateCode
    ? CityLib.getCitiesOfState(iso, stateCode)
    : CityLib.getCitiesOfCountry(iso);
  const options = toOptions(raw || []);
  cityCache.set(key, options);
  return options;
};

/**
 * Keeps a legacy value visible in a dropdown even when it is not part of the
 * dataset (e.g. an old lead saved with country "UAE" or a hand-typed city).
 */
export const withCurrentValue = (options, value) => {
  if (!value) return options;
  const exists = options.some((o) => String(o.value).toLowerCase() === String(value).toLowerCase());
  return exists ? options : [{ value, label: value }, ...options];
};
